import { Trans, useTranslation } from 'react-i18next';

import {
    Alert,
    Body1,
    Button,
    IconWarning,
    Modal,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import { getFragment } from '../../graphql';
import type { DigitallyReceivedPrescriberOrderInfoFragment } from '../../graphql/generated/graphql';
import { PrescriberOrderStatus } from '../../graphql/generated/graphql';
import { useFlags } from '../../hooks/use-flags';

type PrescriptionCancelledAlertProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const PrescriptionCancelledAlert = ({
    prescriberOrder,
}: PrescriptionCancelledAlertProps) => {
    const { t } = useTranslation();
    const [isModalOpen, { open, close }] = useDisclosure();
    const { enableRxCancelMyphx } = useFlags();

    const isCancelled =
        prescriberOrder.status === PrescriberOrderStatus.Cancelled;
    const sentToPharmacy = prescriberOrder.latestSelectedPriceQuoteTimestamp;
    const prescriber = getFragment(prescriberOrder.prescriber);
    const prescriberPhone = prescriber?.phoneNumber;

    if (!isCancelled || !enableRxCancelMyphx) {
        return null;
    }

    return (
        <>
            <Alert
                severity={sentToPharmacy ? 'warning' : 'error'}
                IconOverride={IconWarning}
                primaryButtonProps={
                    !sentToPharmacy
                        ? {
                              label: t(
                                  'prescription.cancelled.moreInformation'
                              ),
                              action: open,
                          }
                        : undefined
                }
            >
                {sentToPharmacy ? (
                    <Trans
                        i18nKey="prescription.cancelled.sentToPharmacy"
                        components={{ 1: <strong /> }}
                    />
                ) : (
                    <Trans
                        i18nKey="prescription.cancelled.notSent"
                        components={{ 1: <strong /> }}
                    />
                )}
            </Alert>

            <Modal
                opened={isModalOpen}
                onClose={close}
                title={t('prescription.cancelled.modal.title')}
            >
                <Stack gap="md">
                    <Body1>{t('prescription.cancelled.modal.body1')}</Body1>
                    <Body1 bold>
                        {t('prescription.cancelled.modal.body2')}
                    </Body1>
                    {prescriberPhone && (
                        <Button
                            onClick={() => {
                                window.location.href = `tel:${prescriberPhone}`;
                            }}
                        >
                            {t('prescription.cancelled.modal.callProvider')}
                        </Button>
                    )}
                </Stack>
            </Modal>
        </>
    );
};
